<template>
  <div>
    <v-row justify="center" align="center">
      <v-col cols="12">
        <v-banner icon="mdi-credit-card-outline">Payment on Account</v-banner>
        <v-card
          color="background"
          flat
          v-if="retrieveAccountCustomerCredit"
          :loading="isLoading"
        >
          <v-form v-model="valid" id="payment-form">
            <AccountCredit class="mt-2" />
            <v-divider></v-divider>
            
            <div v-if="retrieveAccountCustomerCredit.salesLedgerBalance > 0">
              <div v-if="!missingStripeConfig">
                <v-card-title>
                  <!-- -->
                  Choose how much to pay
                </v-card-title>
                <v-card-text>
                  <v-radio-group v-model="currentBalance">
                    <v-radio :value="0" key="0">
                      <template v-slot:label>
                        <div>
                          <strong
                            >£{{
                              retrieveAccountCustomerCredit.salesLedgerBalance
                            }}</strong
                          >
                          - Current Balance
                        </div>
                      </template>
                    </v-radio>
                    <v-radio label="Other Amount" :value="1" key="1"></v-radio>
                  </v-radio-group>
                  <v-row v-if="showAmountField">
                    <v-col cols="8" sm="4" md="3">
                      <v-text-field
                        v-model="amount"
                        outlined
                        :rules="amountValidationRules"
                        type="number"
                        label="Enter Amount"
                        dense
                      ></v-text-field>
                    </v-col>
                  </v-row>
                </v-card-text>
                <v-divider></v-divider>
                <div>
                  <v-card-title>Choose how to pay</v-card-title>
                  <v-card-text v-if="paymentInstruments.length">
                    <v-radio-group v-model="currentCard">
                      <v-radio
                        v-for="(instrument, index) in paymentInstruments"
                        :key="index"
                        :value="instrument.id"
                      >
                        <template v-slot:label>
                          <div>
                            <strong>{{
                              getCardName(instrument.card.brand)
                            }}</strong>
                            - **** {{ instrument.card.last4 }}
                          </div>
                        </template>
                      </v-radio>
                      <v-radio
                        label="New Card"
                        :value="paymentInstruments.length"
                        :key="paymentInstruments.length"
                      ></v-radio>
                    </v-radio-group>
                  </v-card-text>
                  <v-card-text>
                    <v-row class="ma-0 pa-0" v-if="showGooglePay">
                      <v-col cols="12" md="3" class="ma-0 pa-0">
                        <v-list color="#E8E2E1" id="wallet">
                          <v-list-item
                            class="pl-2 pr-0"
                            link
                            @click.stop="payWithGoogle"
                          >
                            <v-list-item-avatar tile min-width="62px">
                              <v-img
                                :src="
                                  require('@/assets/paylogo/GooglePay_mark_800_gray.png')
                                "
                              ></v-img>
                            </v-list-item-avatar>
                            <v-list-item-content>
                              <v-list-item-title>Google Pay</v-list-item-title>
                            </v-list-item-content>
                          </v-list-item>
                        </v-list>
                      </v-col>
                    </v-row>
                    <v-row class="ma-0 pa-0" v-show="showOtherDigitalWalets">
                      <v-col cols="12" md="3" class="ma-0 pa-0">
                        <div id="payment-request-button">
                          <!-- A Stripe Element will be inserted here. -->
                        </div>
                      </v-col>
                    </v-row>
                  </v-card-text>
                  <v-divider></v-divider>
                </div>
                <div v-show="enterNewCard">
                  <v-card-title>
                    <v-icon>home</v-icon>
                    <span class="subtitle-1 font-weight-bold ml-2"
                      >Billing Address</span
                    >
                    <v-checkbox
                      v-model="sameAddress"
                      class="ml-3"
                      label="Same as Account"
                    ></v-checkbox>
                  </v-card-title>
                  <v-card-text>
                    <v-text-field
                      v-model="address.line1"
                      outlined
                      :rules="[required('Address')]"
                      label="Address Line 1"
                    ></v-text-field>
                    <v-text-field
                      v-model="address.line2"
                      outlined
                      label="Address Line 2"
                    ></v-text-field>
                    <v-text-field
                      v-model="address.city"
                      outlined
                      label="City"
                    ></v-text-field>
                    <v-text-field
                      v-model="address.postal_code"
                      outlined
                      :rules="[required('PostCode'), isPostCode]"
                      label="PostCode"
                    ></v-text-field>
                    <v-row v-if="error">
                      <v-col cols="12">
                        <v-icon left color="error" class="mr-2"
                          >announcement</v-icon
                        >
                        <span class="error--text">{{ errorMessage }}</span>
                      </v-col>
                    </v-row>
                  </v-card-text>
                  <v-divider></v-divider>
                  <v-card-title>
                    <v-icon>payment</v-icon>
                    <span class="subtitle-1 font-weight-bold ml-2"
                      >Card Details</span
                    >
                  </v-card-title>
                  <v-card-text>
                    <div id="card-element" class="mt-2">
                      <!--Stripe.js injects the Card Element-->
                    </div>
                    <div
                      id="card-errors"
                      class="mt-2 red--text"
                      role="alert"
                    ></div>
                  </v-card-text>
                  <v-divider></v-divider>
                  <v-card-actions>
                    <v-checkbox
                      v-model="saveCard"
                      label="Save Card for future Payments"
                    ></v-checkbox>
                  </v-card-actions>
                </div>

                <v-card-actions>
                  <v-btn
                    id="submit"
                    block
                    x-large
                    color="primary"
                    :disabled="!valid || hasNoPaymentMethod"
                    @click.stop="makePayment"
                    :loading="processingPayment"
                    v-if="selectedMethod"
                    >Pay {{ buttonAmount }}</v-btn
                  >
                </v-card-actions>
              </div>
              <div v-else class="mt-4">
                <strong
                  >Sorry we do not accept online payments yet.Please call
                  customer service
                </strong>
              </div>
            </div>
            <div v-else class="mt-4">
              <strong>You do not have any outstanding balance to pay</strong>
            </div>
          </v-form>
        </v-card>
 
        <div v-else class="mt-4">
              <strong>There are no customers on file</strong>
            </div>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>
<script>
// import BillingInfoForm from "@/components/auth/DeliveryInfoForm.vue"
import checkPostCode from "@/util/validation/postcode.js";
import AccountCredit from "@/components/account/AccountCredit.vue";
import { mapActions, mapGetters } from "vuex";
export default {
  name: "AccountPayment",
  components: {
    AccountCredit,
  },
  data: () => {
    return {
      valid: false,
      required(fieldName) {
        return (v) => (v && v.length > 0) || `${fieldName} Required`;
      },
      isPostCode: (val) => {
        return checkPostCode(val);
      },

      address: {
        line1: "",
        line2: "",
        city: "",
        postal_code: "",
        country: "GB",
      },
      amount: "",
      stripe: "",
      card: "",
      //  prButton:"",
      paymentRequest: "",
      error: false,
      errorMessage: "",
      sameAddress: true,
      processingPayment: false,
      currentBalance: 0,
      showAmountField: false,
      saveCard: false,
      paymentInstruments: [],
      currentCard: null,
      enterNewCard: false,
      selectedMethod: null,
      isLoading: false,
      showGooglePay: false,
      showOtherDigitalWalets: false,
      missingStripeConfig: false,
    };
  },
  watch: {
    sameAddress(val) {
      if (val == true) {
        this.populateAddress();
      } else {
        this.resetAddress();
      }
    },
    currentBalance(val) {
      if (val == 1) {
        this.showAmountField = true;
        this.amount = "";
      } else {
        this.showAmountField = false;
        this.amount = this.retrieveAccountCustomerCredit.salesLedgerBalance;
      }
    },
    retrieveAccountCustomerCredit(val) {
      if (val) {
        this.amount = this.retrieveAccountCustomerCredit.salesLedgerBalance;
      }
    },
    currentCard(val) {
      if (val == this.paymentInstruments.length) {
        this.loadStripe();
        this.selectedMethod = "new-card";
        this.enterNewCard = true;
      } else {
        this.selectedMethod = val;
        this.enterNewCard = false;
      }
    },
    amount(val) {
      if (val) {
        if (this.paymentRequest) {
          this.updateRequest(val);
        }
      }
    },
  },
  computed: {
    ...mapGetters([
      "retrieveStripeClientKey",
      "retrieveUser",
      "retrieveCustomerAddress",
      "retrieveAccountCustomerCredit",
      "retrieveCustomer",
    ]),
    hasNoPaymentMethod() {
      return this.selectedMethod == null;
    },

    amountValidationRules() {
      return [
        (v) => !!v || "Amount Required",
        (v) =>
          v <= this.retrieveAccountCustomerCredit.salesLedgerBalance ||
          "Amount must not be more than balance",
        (v) => v > 0 || "Invalid Amount",
      ];
    },
    buttonAmount() {
      if (this.amount) {
        return "£ " + parseFloat(this.amount).toFixed(2);
      }
      return "";
    },

    customerAccountNo() {
      if (this.retrieveCustomer != "no_customers") {
        return this.retrieveCustomer.customer.delivery_account_no;
      } else {
        return "0";
      }
    },
  },
  methods: {
    ...mapActions([
      "accountPaymentIntentAction",
      "getCustomer",
      "getSavedPaymentMethods",
    ]),
    populateAddress() {
      this.address.line1 = this.retrieveCustomerAddress.address_line_1;
      this.address.line2 = this.retrieveCustomerAddress.address_line_2;
      this.address.city =
        this.retrieveCustomerAddress.address_line_3 +
        " , " +
        this.retrieveCustomerAddress.address_line_4;
      this.address.postal_code = this.retrieveCustomerAddress.postcode;
    },
    resetAddress() {
      this.address.line1 = "";
      this.address.line2 = "";
      this.address.city = "";
      this.address.postal_code = "";
    },
    makePayment() {
      if (this.valid) {
        this.processingPayment = true;
        this.error = false;
        this.errorMessage = "";
        const rec = Object.assign(
          {},
          { userId: this.retrieveUser.userNum },
          { sessionKey: this.retrieveUser.sessionKey },
          { amount: this.amount },
          { address1: this.address.line1 },
          { address2: this.address.line2 },
          { city: this.address.city },
          { postal_code: this.address.postal_code }
        );
        this.accountPaymentIntentAction(rec).then((response) => {
          if (response.success == true && response.clientSecret) {
            console.log("success from intent side");
            let paymentMethodDetails = this.getPaymentMethodDetails();
            this.stripe
              .confirmCardPayment(response.clientSecret, paymentMethodDetails)
              .then((response) => {
                if (response.error) {
                  console.log("Error processing payment");
                  this.processingPayment = false;
                  this.error = true;
                  this.errorMessage = response.error.message;
                } else {
                  if (response.paymentIntent.status === "succeeded") {
                    this.currentBalance = 0;
                    this.$router.push({
                      name: "PaymentSuccess",
                      params: {
                        td_id: "acc-paid",
                        payment_ref: response.paymentIntent.id,
                      },
                    });
                  }
                }
              });
          } else {
            console.log("problem with intent seting up");
            this.error = true;
            this.errorMessage = response.message;
          }
        });
      }
    },
    loadStripe() {
      let clientToken = this.$store.state.configs.stripe_client_key;
      if (clientToken) {
        this.missingStripeConfig = false;
        this.stripe = Stripe(clientToken);
        this.paymentRequest = this.stripe.paymentRequest({
          country: "GB",
          currency: "gbp",
          total: {
            label: "Payment for account " + this.customerAccountNo,
            amount: this.amount * 100,
          },
          requestPayerName: true,
          requestPayerEmail: true,
        });
        var prButton = this.stripe.elements().create("paymentRequestButton", {
          paymentRequest: this.paymentRequest,
          style: {
            paymentRequestButton: {
              type: "default",
              theme: "dark",
              height: "54px",
            },
          },
        });

        // Check the availability of the Payment Request API first.
        this.paymentRequest.canMakePayment().then((result) => {
          let isChromeBrowser =
            /Google Inc/.test(navigator.vendor) &&
            !/Edg/.test(navigator.userAgent);
          if (result) {
            console.log("isChrome " + isChromeBrowser);
            if (result.applePay == true || !isChromeBrowser) {
              this.showOtherDigitalWalets = true;
              prButton.mount("#payment-request-button");
            } else {
              this.showOtherDigitalWalets = false;
              this.showGooglePay = true;
            }
          } else {
            document.getElementById("payment-request-button").style.display =
              "none";
            this.showOtherDigitalWalets = false;
            this.showGooglePay = false;
          }
        });
        //      prButton.addEventListener('paymentmethod')
        this.paymentRequest.on("paymentmethod", (ev) => {
          this.makeWalletPayment(ev);
        });
        this.card = this.stripe.elements().create("card", {
          style: {
            base: {
              fontWeight: 400,
              fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
              fontSize: "16px",
            },
          },
          hidePostalCode: true,
        });
        this.card.mount("#card-element");
        this.card.addEventListener("change", ({ error }) => {
          const displayError = document.getElementById("card-errors");
          this.error = "";
          this.errorMessage = "";
          if (error) {
            displayError.textContent = error.message;
            this.valid = false;
          } else {
            displayError.textContent = "";
            this.valid = true;
          }
        });
        console.log("Loading Stripe ...");
      } else {
        this.missingStripeConfig = true;
      }
    },
    getCardName(name) {
      return name.charAt(0).toUpperCase() + name.slice(1);
    },
    getPaymentMethods() {
      this.isLoading = true;
      const rec = Object.assign(
        {},
        { userId: this.retrieveUser.userNum },
        { sessionKey: this.retrieveUser.sessionKey }
      );
      this.getSavedPaymentMethods(rec).then((response) => {
        if (response.success == true) {
          this.paymentInstruments = response.sources;
          this.enterNewCard = false;
          this.currentCard = null;
          this.isLoading = false;
        } else {
          console.log(response);
          this.paymentInstruments = [];
          this.enterNewCard = true;
          this.selectedMethod = "new-card";
          this.isLoading = false;
        }
      });
    },
    getPaymentMethodDetails() {
      let cardPaymentDetails = null;
      if (this.selectedMethod == "new-card") {
        cardPaymentDetails = {
          payment_method: {
            card: this.card,
            billing_details: {
              address: this.address,
              email: null,
              name: this.retrieveUser.username,
              phone: this.retrieveCustomerAddress.telephone_no,
            },
          },
        };

        if (this.saveCard == true) {
          cardPaymentDetails.setup_future_usage = "off_session";
        }
      } else {
        cardPaymentDetails = {
          payment_method: this.selectedMethod,
        };
      }
      return cardPaymentDetails;
    },
    makeWalletPayment(ev) {
      console.log(ev);
      const rec = Object.assign(
        {},
        { userId: this.retrieveUser.userNum },
        { sessionKey: this.retrieveUser.sessionKey },
        { amount: this.amount },
        { address1: this.address.line1 },
        { address2: this.address.line2 },
        { city: this.address.city },
        { postal_code: this.address.postal_code }
      );
      this.accountPaymentIntentAction(rec).then((response) => {
        if (response.success == true && response.clientSecret) {
          console.log("success from intent side");
          this.stripe
            .confirmCardPayment(
              response.clientSecret,
              { payment_method: ev.paymentMethod.id },
              { handleActions: false }
            )
            .then((confirmResult) => {
              if (confirmResult.error) {
                ev.complete("fail");
                console.log("Error processing payment");
                this.processingPayment = false;
                this.error = true;
                this.errorMessage = confirmResult.error.message;
              } else {
                ev.complete("success");
                this.currentBalance = 0;
                this.$router.push({
                  name: "PaymentSuccess",
                  params: {
                    td_id: "acc-paid",
                    payment_ref: confirmResult.paymentIntent.id,
                  },
                });
              }
            });
        } else {
          console.log("problem with intent seting up");
          this.error = true;
          this.errorMessage = response.message;
        }
      });
    },
    payWithGoogle() {
      this.paymentRequest.show();
    },
    updateRequest(newVal) {
      if (newVal && this.paymentRequest) {
        this.paymentRequest.update({
          currency: "gbp",
          total: {
            label: "Payment for account " + this.customerAccountNo,
            amount: newVal * 100,
          },
        });
      }
    },
  },

  //This is to ensure that the card.mount happens only after the page
  //has been rendered, else it throws an error
  mounted: function () {
    this.loadStripe();
  },

  async activated() {
    await this.getCustomer();
    await this.getPaymentMethods();
    this.processingPayment = false;
    this.amount = this.retrieveAccountCustomerCredit.salesLedgerBalance;
    this.populateAddress();
  },
};
</script>
<style scoped>
#wallet {
  border-radius: 12px;
}
</style>
